/**
 * Staff-facing EPA ID tracking for waste facilities: crosses "does the
 * governing federal generator status require an EPA ID" with "is a live EPA
 * ID recorded". Null/absent for facilities without the waste program.
 */
export const EPA_ID_REQUIREMENT_STATUSES = [
  "HAS_EPA_ID",
  "NEEDS_EPA_ID",
  "EPA_ID_EXPIRED",
  "NOT_REQUIRED",
  "NOT_REQUIRED_HAS_EPA_ID",
] as const;

export type EpaIdRequirementStatus = (typeof EPA_ID_REQUIREMENT_STATUSES)[number];

export function isEpaIdRequirementStatus(value: string): value is EpaIdRequirementStatus {
  return (EPA_ID_REQUIREMENT_STATUSES as readonly string[]).includes(value);
}

/** States whose programs require even federal VSQGs to obtain an EPA ID. */
export const STATES_REQUIRING_EPA_ID_FOR_VSQG = [
  "CA",
  "LA",
  "MA",
  "ME",
  "MN",
  "NH",
  "RI",
  "VT",
  "WV",
] as const;

export function stateRequiresEpaIdForVsqg(state: string | null | undefined): boolean {
  return state != null && (STATES_REQUIRING_EPA_ID_FOR_VSQG as readonly string[]).includes(state);
}

/** States where shipping on a Uniform Hazardous Waste Manifest requires an EPA ID. */
export const STATES_REQUIRING_EPA_ID_FOR_UNIFORM_MANIFEST = ["WI"] as const;

export function stateRequiresEpaIdForUniformManifest(state: string | null | undefined): boolean {
  return (
    state != null &&
    (STATES_REQUIRING_EPA_ID_FOR_UNIFORM_MANIFEST as readonly string[]).includes(state)
  );
}

export const EPA_ID_REQUIREMENT_STATUS_LABELS: Record<EpaIdRequirementStatus, string> = {
  HAS_EPA_ID: "Has EPA ID",
  NEEDS_EPA_ID: "Needs EPA ID",
  EPA_ID_EXPIRED: "EPA ID expired",
  NOT_REQUIRED: "Not required",
  NOT_REQUIRED_HAS_EPA_ID: "Has EPA ID (not required)",
};

/** Why a facility requires an EPA ID — one entry per rule that fired. */
export const EPA_ID_REQUIREMENT_REASONS = [
  "GENERATOR_STATUS",
  "HISTORICAL_GENERATOR_STATUS",
  "STATE_VSQG_RULE",
  "STATE_UNIFORM_MANIFEST_RULE",
] as const;

export type EpaIdRequirementReason = (typeof EPA_ID_REQUIREMENT_REASONS)[number];

export const EPA_ID_REQUIREMENT_REASON_LABELS: Record<EpaIdRequirementReason, string> = {
  GENERATOR_STATUS: "SQG/LQG generator status",
  HISTORICAL_GENERATOR_STATUS: "Prior-year generator status (no current-year data)",
  STATE_VSQG_RULE: "State requires VSQGs to hold an EPA ID",
  STATE_UNIFORM_MANIFEST_RULE:
    "State requires an EPA ID to ship on a Uniform Hazardous Waste Manifest",
};

/** Display label for a status; unknown values fall through unchanged. */
export function epaIdRequirementStatusLabel(value: string): string {
  return isEpaIdRequirementStatus(value) ? EPA_ID_REQUIREMENT_STATUS_LABELS[value] : value;
}

/** Export-cell label: empty string for non-waste facilities (null/undefined). */
export function epaIdRequirementStatusExportLabel(
  status: EpaIdRequirementStatus | null | undefined
): string {
  return status ? EPA_ID_REQUIREMENT_STATUS_LABELS[status] : "";
}

/** Federal tiers that require an EPA ID in every state. */
const EPA_ID_REQUIRING_FEDERAL_STATUSES: ReadonlySet<string> = new Set([
  "LARGE_QUANTITY_GENERATOR",
  "SMALL_QUANTITY_GENERATOR",
]);

/**
 * Whether a month has no reported generator status to compare against, so the
 * reported-vs-calculated threshold comparison should not run for it at all.
 *
 * Callers pass the whole month; the reported-status check lives here so a
 * caller cannot apply the requirement rule and forget it.
 */
export function shouldSkipThresholdComparison({
  reportedFederalStatus,
  requirementStatus,
  calculatedFederalStatus,
}: {
  reportedFederalStatus: string | null | undefined;
  requirementStatus: EpaIdRequirementStatus | null | undefined;
  calculatedFederalStatus: string | null | undefined;
}): boolean {
  // A reported status exists: compare against it.
  if (reportedFederalStatus != null) {
    return false;
  }
  // The facility owes an EPA ID (or holds one). Reported status is read from
  // RCRAInfo through that ID, so its absence is the finding, not an exemption.
  if (requirementStatus !== "NOT_REQUIRED") {
    return false;
  }
  // The facility needs no EPA ID overall, but the requirement is derived from
  // the `FacilityEPAIds` view, which only looks at the current year. An
  // earlier, still-viewable month that reached SQG/LQG needed one at the time.
  return !EPA_ID_REQUIRING_FEDERAL_STATUSES.has(calculatedFederalStatus ?? "");
}

/**
 * Presence and requirement classify independently (a VSQG can hold an ID).
 * With no live ID, an expired one wins over the requirement axis — the
 * lapsed association is the more specific gap.
 */
export function classifyEpaIdRequirementStatus({
  requiresEpaId,
  hasLiveEpaId,
  hasExpiredEpaId,
}: {
  requiresEpaId: boolean;
  hasLiveEpaId: boolean;
  hasExpiredEpaId: boolean;
}): EpaIdRequirementStatus {
  if (hasLiveEpaId) {
    return requiresEpaId ? "HAS_EPA_ID" : "NOT_REQUIRED_HAS_EPA_ID";
  }
  if (hasExpiredEpaId) {
    return "EPA_ID_EXPIRED";
  }
  return requiresEpaId ? "NEEDS_EPA_ID" : "NOT_REQUIRED";
}
